import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import type { OnChangeFn, RowSelectionState, SortingState } from '@tanstack/react-table';
import { useDebounce } from '@uidotdev/usehooks';
import { useState } from 'react';

import { ChevronDown, Minus, Plus } from '@repo/media/lucide';

import { Button } from '@repo/components/button';
import { Card } from '@repo/components/card';
import { Menu, MenuItem, MenuSection, MenuTrigger } from '@repo/components/menu';
import { Popover } from '@repo/components/popover';
import { SearchField } from '@repo/components/search-field';

import { isDefined } from '@repo/utils/is-defined';
import { normalise } from '@repo/utils/normalise';
import { pluralize } from '@repo/utils/pluralize';

import { FeatureGuard } from '~/features/auth/feature-guard.tsx';
import { useFeatureGuard } from '~/features/auth/use-feature-guard.ts';
import { queryOrganizationRoles, queryOrganizationUsers } from '~/services/authorization/authorization.ts';
import type { PersonUser } from '~/services/authorization/types.ts';

import { DialogAddUserByEmail } from './-components/dialog-add-user-by-email.tsx';
import { DialogAssignRolesBulk } from './-components/dialog-assign-roles-bulk.tsx';
import { DialogRemoveRolesBulk } from './-components/dialog-remove-roles-bulk.tsx';
import { DialogSetRoles } from './-components/dialog-set-roles.tsx';
import { UsersTable } from './-components/users-table.tsx';

const pageSize = 25;

export const Route = createFileRoute('/(user-mgmt)/organization/$org/user-mgmt/users')({
  loader: async ({ context: { queryClient }, params: { org } }) => {
    await Promise.all([
      queryClient.ensureQueryData(queryOrganizationUsers(org, { page: 1, pageSize })),
      queryClient.ensureQueryData(queryOrganizationRoles(org)),
    ]);
  },
  component: RouteComponent,
});

type Dialog = { type: 'set' | 'assign-bulk' | 'remove-bulk' | 'add-by-email' };

// The active role filter, applied server-side. `roleId` targets a specific role;
// `roleType` covers synthetic role buckets that aren't individual custom roles.
type RoleFilter = { label: string; roleId?: string; roleType?: 'SuperAdmin' | 'Admin' | 'Custom' };

function RouteComponent() {
  const orgId = Route.useParams().org;
  const { hasFeature } = useFeatureGuard();
  const [dialog, setDialog] = useState<Dialog>();
  const [dialogUser, setDialogUser] = useState<PersonUser>();
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [roleFilter, setRoleFilter] = useState<RoleFilter>();
  const [sorting, setSorting] = useState<SortingState>([]);

  const usersResult = useSuspenseQuery(queryOrganizationUsers(orgId, {
    searchText: debouncedSearchTerm,
    page: currentPage,
    pageSize,
    roleId: roleFilter?.roleId,
    roleType: roleFilter?.roleType,
    sortBy: sorting[0]?.id as 'name' | 'email' | undefined,
    sortDescending: sorting[0]?.desc,
  })).data;
  const users = usersResult.items;
  const roles = useSuspenseQuery(queryOrganizationRoles(orgId)).data;
  const rolesById = normalise(roles).byId;
  const customRoles = roles.filter(role => role.type === 'Custom');
  const canManageUsers = hasFeature({ key: 'user-management:manage-users', scope: 'Organization' });
  const canRegisterUsers = hasFeature({ key: 'user-management:register-user', scope: 'Organization' });

  const clearFilter = () => {
    setSearchTerm('');
    setRoleFilter(undefined);
    setCurrentPage(1);
    setRowSelection({});
  };

  // Re-sorting reorders the entire (server-side) result set, so drop back to the first page
  // and clear any page-local selection.
  const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
    setSorting(prev => (typeof updater === 'function' ? updater(prev) : updater));
    setCurrentPage(1);
    setRowSelection({});
  };

  const selectedUsers = Object.entries(rowSelection)
    .map(([idx, selected]) => (selected ? users[Number(idx)] : undefined))
    .filter(isDefined);
  const selectedCount = selectedUsers.length;
  const userForRoleDialog = dialogUser ?? selectedUsers[0];
  const hasActiveFilters = Boolean(searchTerm || roleFilter);

  return (
    <>
      {dialog && (
        <>
          {userForRoleDialog && (
            <DialogSetRoles
              type="organization"
              parentId={orgId}
              roles={roles}
              user={userForRoleDialog}
              open={dialog.type === 'set'}
              close={() => {
                setDialogUser(undefined);
                setDialog(undefined);
              }}
            />
          )}
          <DialogAssignRolesBulk
            open={dialog.type === 'assign-bulk'}
            close={() => {
              setDialog(undefined);
            }}
            roles={customRoles}
            users={selectedUsers}
            type="organization"
            parentId={orgId}
          />
          <DialogRemoveRolesBulk
            open={dialog.type === 'remove-bulk'}
            close={() => {
              setDialog(undefined);
            }}
            roles={customRoles}
            users={selectedUsers}
            type="organization"
            parentId={orgId}
          />
          <DialogAddUserByEmail
            open={dialog.type === 'add-by-email'}
            close={() => {
              setDialog(undefined);
            }}
            parentId={orgId}
            openRoleAssignmentOnSuccess={canManageUsers}
            onRegistered={(user) => {
              setRowSelection({});

              if (canManageUsers) {
                setDialogUser(user);
                setDialog({ type: 'set' });
                return;
              }

              setDialog(undefined);
            }}
          />
        </>
      )}

      <div className="flex gap-4 flex-wrap items-center">
        <SearchField
          name="search"
          autoComplete="off"
          aria-label="Search users"
          placeholder="Search users"
          value={searchTerm}
          onChange={(val) => {
            setSearchTerm(val);
            setCurrentPage(1);
            setRowSelection({});
          }}
        />
        <MenuTrigger>
          <Button variant="soft" icon={{ component: <ChevronDown className="-m-1" /> }}>
            {roleFilter?.label ?? 'All roles'}
          </Button>
          <Popover>
            <Menu>
              <MenuSection title="Filter role">
                <MenuItem onAction={() => {
                  setRoleFilter(undefined);
                  setCurrentPage(1);
                  setRowSelection({});
                }}
                >
                  All roles
                </MenuItem>
                {roles.map(role => (
                  <MenuItem
                    key={role.id}
                    onAction={() => {
                      setRoleFilter({ label: role.name, roleId: role.id });
                      setCurrentPage(1);
                      setRowSelection({});
                    }}
                  >
                    {role.name}
                  </MenuItem>
                ))}
              </MenuSection>
            </Menu>
          </Popover>
        </MenuTrigger>

        <FeatureGuard features={{ key: 'user-management:register-user', scope: 'Organization' }}>
          <Button action={() => { setDialog({ type: 'add-by-email' }); }}>
            Add user by email
          </Button>
        </FeatureGuard>

        <FeatureGuard features={{ key: 'user-management:manage-users', scope: 'Organization' }}>
          {selectedCount === 1 && (
            <Button action={() => {
              setDialogUser(selectedUsers[0]);
              setDialog({ type: 'set' });
            }}
            >
              Manage roles
            </Button>
          )}
          {selectedCount > 1 && (
            <MenuTrigger>
              <Button icon={{ component: <ChevronDown className="-m-1" /> }}>
                Manage roles (
                {pluralize(selectedCount, 'user')}
                )
              </Button>
              <Popover>
                <Menu>
                  <MenuItem onAction={() => { setDialog({ type: 'assign-bulk' }); }}>
                    <Plus size={18} />
                    Assign roles
                  </MenuItem>
                  <MenuItem onAction={() => { setDialog({ type: 'remove-bulk' }); }}>
                    <Minus size={18} />
                    Remove roles
                  </MenuItem>
                </Menu>
              </Popover>
            </MenuTrigger>
          )}
        </FeatureGuard>
      </div>

      <Card className="mt-4 max-w-full w-fit p-0">
        <UsersTable
          type="organization"
          users={users}
          roles={rolesById}
          rowSelection={rowSelection}
          setRowSelection={setRowSelection}
          sorting={sorting}
          onSortingChange={handleSortingChange}
          currentPage={currentPage}
          pageCount={Math.max(usersResult.totalPages, 1)}
          onPageChange={(page) => {
            setCurrentPage(page);
            setRowSelection({});
          }}
          noResults={(
            <div className="flex flex-col items-start gap-4">
              {hasActiveFilters
                ? (
                    <>
                      <p>Found no users matching the current filter.</p>
                      <Button action={clearFilter}>Clear filter</Button>
                    </>
                  )
                : (
                    <>
                      <p>No users found.</p>
                      {canRegisterUsers && (
                        <Button action={() => { setDialog({ type: 'add-by-email' }); }}>
                          Add user by email
                        </Button>
                      )}
                    </>
                  )}
            </div>
          )}
        />
      </Card>
    </>
  );
}
